import {portalHeadingDelta, resolvePortalPair, transformPortalPoint} from './portal-math.mjs';

const finite = (value, fallback = 0) => Number.isFinite(value) ? value : fallback;
const wrap = angle => Math.atan2(Math.sin(angle), Math.cos(angle));
const validId = value => typeof value === 'string' && value.length > 0;
const crossingSeq = player => Number.isSafeInteger(player.crossingSeq) && player.crossingSeq >= 0
  ? player.crossingSeq : Number.isSafeInteger(player.lastCrossing?.seq) ? player.lastCrossing.seq : 0;
const appearance = value => value && typeof value === 'object' && !Array.isArray(value) ? {...value} : {};
const appearanceKey = value => JSON.stringify(Object.keys(value).sort().map(key => [key, value[key]]));

/** Validate the entire replacement before changing any membership. A malformed
 * or partial roster must never evict otherwise valid remote players. */
function readSnapshot(frame, lastSeq, selfId) {
  if (frame?.type !== 'presence' || !Number.isSafeInteger(frame.snapshotSeq) || frame.snapshotSeq <= lastSeq
    || !Array.isArray(frame.worldIds) || !Array.isArray(frame.worlds)) return null;
  const ids = new Set(frame.worldIds), revisions = new Map(), players = new Map(), seen = new Set();
  if (ids.size !== frame.worldIds.length || ids.size !== frame.worlds.length || [...ids].some(id => !validId(id))) return null;
  for (const world of frame.worlds) {
    if (!world || !ids.has(world.worldId) || revisions.has(world.worldId) || !Array.isArray(world.players)
      || !Number.isSafeInteger(world.revision) || world.revision < 0) return null;
    revisions.set(world.worldId, world.revision);
    if (world.npcs !== undefined && (!Array.isArray(world.npcs) || world.npcs.length > 4 || world.npcs.some(npc => npc?.kind !== 'npc' || !npc.id?.startsWith('npc:')))) return null;
    for (const player of [...world.players, ...(world.npcs ?? [])]) {
      if (!player || !validId(player.id) || seen.has(player.id) || player.worldId !== world.worldId
        || !['x', 'y', 'z'].every(axis => Number.isFinite(player.position?.[axis]))) return null;
      seen.add(player.id);
      if (player.id === selfId) continue;
      players.set(player.id, {...player, avatar: appearance(player.avatar), position: {...player.position},
        portalTraversal: player.portalTraversal ? {...player.portalTraversal} : null,
        portalApproach: player.portalApproach ? {...player.portalApproach} : null,
        lastCrossing: player.lastCrossing ? {...player.lastCrossing} : null});
    }
  }
  return {ids, revisions, players};
}

/** Canonical, presentation-only remote avatars. Complete presence frames own
 * membership; changing the local world or replacing a cached scene does not.
 * There is one avatar/mixer per player, no movement prediction or authority.
 * Call update once before portal-straddling sync and all scene renders.
 *
 * ensureWorld(id, revision) should fetch/register a shared cached world view.
 * selfId may be a function if the local player identity is assigned on welcome.
 * clear() handles disconnect/reconnect; dispose() permanently closes this owner.
 * Boundary travellers outside the server's observed world set are not included.
 */
export function createRemotePresence({selfId, createAvatar, getWorldView, getWorld, ensureWorld,
  removeContinuation = () => {}, parkContinuation = () => false, isContinuationRetained = () => false, identity, onError = () => {}, now = () => performance.now()}) {
  if (typeof createAvatar !== 'function' || typeof getWorldView !== 'function' || typeof getWorld !== 'function') {
    throw new TypeError('Remote presence requires avatar and world-view factories');
  }
  const records = new Map(), loads = new Map(), retained = new Map();
  let retention = null;
  const ownIdentity = () => typeof identity === 'function' ? identity() : ownId();
  let worldIds = new Set(), revisions = new Map(), lastSeq = -1, disposed = false;
  let serverClock=null,clockReceivedAt=0;
  const ownId = () => typeof selfId === 'function' ? selfId() : selfId;
  function report(error, id) { try { onError(error, id); } catch { /* reporting must not break cleanup */ } }
  function removeSplit(record) {
    try { removeContinuation(`player:${record.id}`); } catch (error) { report(error, record.id); }
  }
  function detach(record) {
    if (!record.avatar || (!record.avatar.root.parent && !record.attachedScene)) return;
    removeSplit(record);
    record.avatar.root.removeFromParent();
    record.attachedScene = null;
  }
  function release(record) {
    records.delete(record.id); retained.delete(record.id);
    removeSplit(record);
    record.avatar?.root.removeFromParent();
    try { record.avatar?.dispose?.(); } catch (error) { report(error, record.id); }
  }
  function current(record) { return !disposed && records.get(record.id) === record; }
  function readyView(id) {
    const world = getWorld(id), view = getWorldView(id), revision = revisions.get(id) ?? 0;
    if (!world || !view?.scene || world.revision < revision || (view.world && view.world.revision < revision)) return null;
    return view;
  }
  function requestWorld(id) {
    if (typeof ensureWorld !== 'function' || disposed || !worldIds.has(id)) return;
    const previous = loads.get(id), revision = revisions.get(id) ?? 0;
    if (previous?.pending || (previous?.revision === revision && Date.now() < previous.retryAt)) return;
    const load = {revision, pending: true, retryAt: Infinity};
    loads.set(id, load);
    let result;
    try { result = ensureWorld(id, revision); } catch (error) { result = Promise.reject(error); }
    Promise.resolve(result).catch(error => {
      if (!disposed && loads.get(id) === load) report(error, id);
    }).finally(() => {
      if (disposed || loads.get(id) !== load) return;
      load.pending = false; load.retryAt = Date.now() + 1000;
      // A completed old load may not restore unsubscribed players or old poses.
      for (const record of records.values()) if (record.player.worldId === id) attach(record);
    });
  }
  function attach(record) {
    if (!record.avatar || !current(record)) return;
    const id = record.player.worldId, view = readyView(id), root = record.avatar.root;
    if (!view) { detach(record); requestWorld(id); return; }
    if (root.parent !== view.scene) {
      // A freshly reconciled retained root has no live clipping or ghost. Its
      // native parked handle is validated again on the next straddling sync.
      if (!record.reusing) removeSplit(record);
      root.removeFromParent();
      view.scene.add(root);
    }
    record.attachedScene = view.scene; record.reusing = false;
  }
  function customize(record) {
    if (!current(record) || !record.avatar || record.appearanceKey === record.desiredKey) return;
    record.appearanceKey = record.desiredKey;
    removeSplit(record);
    let result;
    try { result = record.avatar.customize?.(record.desiredAvatar); } catch (error) { report(error, record.id); return; }
    if (!result || typeof result.then !== 'function') return;
    // The avatar owns latest-request cancellation. Forward a newer preset
    // immediately, so a slow prior request cannot delay or overwrite it.
    Promise.resolve(result).catch(error => { if (current(record)) report(error, record.id); });
  }
  function makeAvatar(record) {
    try {
      const avatar = createAvatar(record.player.avatar, record.player);
      if (!avatar?.root?.position || !avatar.root.rotation) throw new TypeError('Remote avatar requires a stable root');
      record.avatar = avatar;
      record.appearanceKey = record.desiredKey;
      avatar.root.position.copy(record.player.position);
      avatar.root.rotation.y = finite(record.player.yaw);
    } catch (error) { report(error, record.id); }
  }
  function crossingPair(previous, next) {
    const crossing = next.lastCrossing, before = crossingSeq(previous), after = crossingSeq(next);
    if (!crossing || after !== before + 1 || crossing.seq !== after || !validId(crossing.id)
      || crossing.id === previous.lastCrossing?.id || crossing.fromWorldId !== previous.worldId
      || crossing.toWorldId !== next.worldId) return null;
    const sourceWorld = getWorld(previous.worldId), targetWorld = getWorld(next.worldId);
    const source = sourceWorld?.portals?.find(portal => portal.id === crossing.sourcePortalId);
    const pair = sourceWorld && resolvePortalPair(sourceWorld, source, new Map([[previous.worldId, sourceWorld], [next.worldId, targetWorld]]));
    if (!pair || pair.world.id !== next.worldId || pair.target.id !== crossing.targetPortalId) return null;
    return {...pair,sourceSide:crossing.sourceSide===-1?-1:1,targetSide:crossing.targetSide===-1?-1:1};
  }
  function setPlayer(record, player) {
    const previous = record.player, root = record.avatar?.root;
    const changedSpace = previous.worldId !== player.worldId || crossingSeq(previous) !== crossingSeq(player);
    if (root && changedSpace) {
      record.waveUntil=0;
      // Remove the old split representation before modifying its source pose.
      removeSplit(record); root.removeFromParent();
      const pair = crossingPair(previous, player);
      if (pair) {
        root.position.copy(transformPortalPoint(root.position, pair.source, pair.target,pair.sourceSide,pair.targetSide));
        root.rotation.y = wrap(root.rotation.y + portalHeadingDelta(pair.source, pair.target,pair.sourceSide,pair.targetSide));
      } else {
        // Never interpolate between unrelated spaces, including a missed loop
        // which happens to arrive back in the same world.
        root.position.copy(player.position); root.rotation.y = finite(player.yaw);
      }
    }
    record.player = player;
    record.desiredAvatar = player.avatar; record.desiredKey = appearanceKey(player.avatar);
    if (!record.avatar) makeAvatar(record); else customize(record);
    attach(record);
  }
  function applySnapshot(frame) {
    if (disposed) return false;
    // A delayed pre-entry roster is not evidence that anyone is still present.
    if (retention && (!Number.isSafeInteger(frame?.requestSeq) || frame.requestSeq < retention.requestSeq || !validId(frame.baseWorldId))) return false;
    const snapshot = readSnapshot(frame, lastSeq, ownId());
    if (!snapshot) return false;
    lastSeq = frame.snapshotSeq; worldIds = snapshot.ids; revisions = snapshot.revisions;
    if (retention) {
      const sameOwner = frame.baseWorldId === retention.worldId && retention.selfId === ownId() && retention.identity === ownIdentity();
      for (const record of retained.values()) {
        const player = snapshot.players.get(record.id), root = record.avatar?.root;
        const unchanged = sameOwner && player && player.worldId === retention.worldId
          && snapshot.revisions.get(player.worldId) === record.retainedRevision
          && getWorld(player.worldId)?.revision === record.retainedRevision
          && (getWorldView(player.worldId)?.world?.revision ?? record.retainedRevision) === record.retainedRevision
          && readyView(player.worldId)?.scene.uuid === record.retainedSceneId
          && appearanceKey(player.avatar) === record.appearanceKey
          && player.kind === record.player.kind && player.name === record.player.name
          && player.accountId === record.player.accountId
          && root?.userData.avatarModelRevision === record.retainedModelRevision
          && !record.avatar.loading && !record.avatar.error && record.avatar.body?.userData.avatarUnavailable !== true
          && isContinuationRetained(`player:${record.id}`);
        if (!unchanged) { release(record); continue; }
        retained.delete(record.id); records.set(record.id, record);
        record.player = player; record.waveUntil = 0; record.reusing = true;
        root.position.copy(player.position); root.rotation.y = finite(player.yaw);
        root.visible = player.kind !== 'npc' || finite(player.visibility, 1) > 0;
        record.avatar.setPresence?.(player.kind === 'npc' ? player.visibility : 1);
        record.avatar.setSocial?.(player.kind === 'npc' ? player.social : null);
      }
      retention = null;
    }
    if(Number.isFinite(frame.serverTime)){serverClock=frame.serverTime;clockReceivedAt=now();}
    for (const id of loads.keys()) if (!worldIds.has(id)) loads.delete(id);
    for (const record of records.values()) if (!snapshot.players.has(record.id)) release(record);
    for (const [id, player] of snapshot.players) {
      let record = records.get(id);
      if (!record) {
        record = {id, player, avatar: null, desiredAvatar: player.avatar, desiredKey: appearanceKey(player.avatar), appearanceKey: null};
        records.set(id, record);
      }
      setPlayer(record, player);
    }
    return true;
  }
  function gesture(message,currentWorldId){
    const record=records.get(message?.playerId),time=now();
    if(disposed||serverClock===null||!record||record.player.kind==='npc'||message.type!=='avatarGesture'||message.gesture!=='wave'
      ||message.worldId!==currentWorldId||record.player.worldId!==currentWorldId||message.durationMs!==2000
      ||!Number.isFinite(message.serverTime)||!Number.isSafeInteger(message.seq)||message.seq<1||message.seq<=(record.gestureSeq??0))return false;
    record.gestureSeq=message.seq;
    const age=serverClock+Math.max(0,time-clockReceivedAt)-message.serverTime,player=record.player;
    // Live only: never queue a greeting for an unknown/late-joining avatar or
    // replay a delayed event after reconnect, movement, flight or world changes.
    if(age>500||age< -1000||player.grounded!==true||player.flying===true||Math.hypot(finite(player.velocityX),finite(player.velocityZ))>=.35)return false;
    record.waveUntil=time+2000-Math.max(0,age);return true;
  }
  function update(dt, time, currentWorldId) {
    if (disposed) return;
    const seconds = Math.min(.1, Math.max(0, finite(dt))), alpha = 1 - Math.exp(-12 * seconds), self = ownId();
    for (const record of records.values()) {
      if (record.id === self) { release(record); continue; }
      const {avatar, player} = record;
      if (!avatar) continue;
      attach(record);
      avatar.setPresence?.(player.kind==='npc'?player.visibility:1);
      avatar.setSocial?.(player.kind==='npc'?player.social:null);
      for (const axis of ['x', 'y', 'z']) avatar.root.position[axis] += (player.position[axis] - avatar.root.position[axis]) * alpha;
      avatar.root.rotation.y = wrap(avatar.root.rotation.y + wrap(finite(player.yaw) - avatar.root.rotation.y) * alpha);
      // Portal displacement and visual interpolation are never locomotion speed.
      const speed=Math.hypot(finite(player.velocityX),finite(player.velocityZ));
      if(speed>=.35||player.grounded!==true||player.flying===true||(currentWorldId&&player.worldId!==currentWorldId))record.waveUntil=0;
      avatar.update?.(seconds, finite(time), speed, !player.grounded, now()<(record.waveUntil??0), {flying:player.flying===true,flightSpeed:Math.hypot(player.velocityX??0,player.velocityY??0,player.velocityZ??0),...(record.gestureSeq?{boundedWave:true,waveSequence:record.gestureSeq}:{})});
    }
  }
  function descriptors() {
    if (disposed) return [];
    const result = [];
    for (const record of records.values()) {
      const root = record.avatar?.root, world = getWorld(record.player.worldId), view = readyView(record.player.worldId);
      if (record.id === ownId() || !root || !view || root.parent !== view.scene) continue;
      result.push({id: `player:${record.id}`, root, world, activePortalId: record.player.activePortalId, portalTraversal: record.player.portalTraversal, portalApproach: record.player.portalApproach, radius: 1.2});
    }
    return result;
  }
  function suspend(worldId, {requestSeq} = {}) {
    if (disposed) return false;
    // Repeated movement resets must not discard a still-hidden pending cache.
    if (retention && retention.worldId === worldId && retention.selfId === ownId() && retention.identity === ownIdentity() && Number.isSafeInteger(requestSeq) && requestSeq >= 0) {
      retention.requestSeq = Math.max(retention.requestSeq, requestSeq); return true;
    }
    for (const record of retained.values()) release(record);
    retention = validId(worldId) && Number.isSafeInteger(requestSeq) && requestSeq >= 0
      ? {worldId, requestSeq, selfId:ownId(), identity:ownIdentity()} : null;
    for (const record of records.values()) {
      const root = record.avatar?.root, view = readyView(record.player.worldId);
      if (!retention || record.player.worldId !== worldId || retained.size >= 8 || !root || !view
        || record.avatar.loading || record.avatar.error || !parkContinuation(`player:${record.id}`)) { release(record); continue; }
      records.delete(record.id); retained.set(record.id, record);
      record.retainedRevision = revisions.get(worldId); record.retainedSceneId = view.scene.uuid;
      record.retainedModelRevision = root.userData.avatarModelRevision;
      record.avatar.resetTransient?.(); record.waveUntil = 0;
      root.removeFromParent(); root.visible = false; record.attachedScene = null;
    }
    loads.clear(); worldIds.clear(); revisions.clear(); serverClock = null;
    return !!retention;
  }
  function clear() {
    for (const record of records.values()) release(record);
    for (const record of retained.values()) release(record);
    retention = null;
    loads.clear(); worldIds.clear(); revisions.clear(); lastSeq = -1;serverClock=null;
  }
  return {applySnapshot, update, descriptors, gesture, suspend,
    mapParticipants(worldId) { return [...records.values()].filter(r=>r.player.worldId===worldId).map(r=>({...r.player,position:{...(r.avatar?.root?.position??r.player.position)}})); },
    residents(worldId) { return [...records.values()].filter(r=>r.player.kind==='npc'&&r.player.worldId===worldId).map(r=>({...r.player,position:{...r.player.position}})); },
    greet(message) { const record=records.get(message?.npcId);if(message?.authored===true&&record?.player.kind==='npc'&&record.player.worldId===message.worldId&&typeof message.text==='string'&&message.text.length<=180)record.avatar?.speak?.(message.text); }, getWorldIds: () => [...worldIds], clear,
    dispose() { if (!disposed) { clear(); disposed = true; } }};
}
